import React from 'react';
import { Save, FolderOpen, Copy, CheckCircle2, Circle, Clock } from 'lucide-react';

/**
 * PlanMessage Component
 * Renders a Gemini-style plan document with structured sections and action buttons
 * 
 * @param {Object} props
 * @param {Object} props.plan - The plan object containing title, goal, timeframe, and sections
 * @param {Function} props.onSaveToSwirl - Callback for "Save to Swirl" action
 * @param {Function} props.onSaveToFocus - Callback for "Save to Focus" action
 * @param {Function} props.onDuplicateInNewChat - Callback for "Duplicate in new chat" action
 */
export default function PlanMessage({ 
  plan, 
  onSaveToSwirl, 
  onSaveToFocus, 
  onDuplicateInNewChat 
}) {
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'done':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'not_started':
      default:
        return <Circle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'done':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            <CheckCircle2 className="w-3 h-3" />
            Done
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            <Clock className="w-3 h-3" />
            In Progress
          </span>
        );
      case 'not_started':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
            <Circle className="w-3 h-3" />
            Not Started
          </span>
        );
    }
  };

  const handleSaveToSwirl = () => {
    console.log('Save to Swirl', plan.id);
    // TODO: Implement save to Swirl backend call
    // This will save the plan to the Swirl section (pending feature)
    if (onSaveToSwirl) {
      onSaveToSwirl(plan);
    }
  };

  const handleSaveToFocus = () => {
    console.log('Save to Focus', plan.id);
    // TODO: Implement save to Focus backend call
    // This should:
    // 1. Check if user has an active focus
    // 2. If yes, show section selector modal
    // 3. If no, show create focus modal with this plan as initial content
    // 4. Save plan to selected focus section
    if (onSaveToFocus) {
      onSaveToFocus(plan);
    }
  };

  const handleDuplicateInNewChat = () => {
    console.log('Duplicate in new chat', plan.id);
    // TODO: Implement duplicate in new chat
    // This should:
    // 1. Create a new chat conversation
    // 2. Add this plan as the first message
    // 3. Navigate to the new chat
    if (onDuplicateInNewChat) {
      onDuplicateInNewChat(plan);
    }
  };

  return (
    <div className="flex justify-start my-6">
      <div className="max-w-3xl w-full">
        {/* AI Avatar */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#7A47F5] to-[#9D6FF5] shadow-sm p-1.5">
            <svg 
              viewBox="500 300 1100 1400" 
              className="w-full h-full"
              preserveAspectRatio="xMidYMid meet"
            >
              <path 
                fill="white" 
                d="M 1164.22 315.416 C 1226.57 311.925 1301.55 320.971 1352.42 359.073 C 1389.34 386.728 1415.89 420.703 1421.8 466.98 C 1429.24 525.221 1418.64 581.381 1382.07 628.153 C 1334.13 689.473 1262.07 738.867 1183.74 748.707 C 1145.35 754.215 1101.89 750.712 1069.86 726.381 C 1046.32 708.505 1026.99 663.003 1048.02 636.427 C 1052.28 631.046 1063.15 624.143 1070.51 624.061 C 1095.77 623.782 1088.83 661.424 1107.65 672.179 C 1124.04 683.691 1146.6 686.047 1165.23 683.95 C 1209.4 678.977 1249.12 651.074 1276.51 617.326 C 1306 581.987 1317.83 539.557 1312.81 493.817 C 1303.37 407.762 1206.47 377.994 1132.28 388.792 C 1035.81 402.834 941.899 455.397 880.433 530.823 C 830.256 592.395 813.55 679.242 865.397 744.693 C 871.503 752.547 878.103 760.229 885.227 767.201 C 955.139 835.617 1047.3 878.027 1131.31 926.345 C 1188.27 959.099 1244.86 989.759 1296.98 1030.25 C 1356.72 1076.65 1403.27 1137.51 1411.7 1214.95 C 1421.33 1300.6 1393.24 1378.11 1339.97 1444.28 C 1248.37 1558.07 1100.97 1622.81 958.279 1638.76 C 846.435 1648.92 726.537 1629.99 636.56 1559.29 C 534.114 1478.79 526.595 1333.69 608.073 1235.77 C 654.169 1180.37 718.444 1134.95 791.291 1125.04 C 811.442 1122.3 849.065 1122.78 851.429 1150.12 C 853.464 1178.04 815.603 1191.49 796.211 1205.52 C 782.773 1215.12 770.559 1226.33 759.838 1238.89 C 695.15 1313.3 678.282 1428.11 759.769 1496.07 C 814.706 1541.88 889.784 1555.81 959.625 1551.15 C 1047.74 1545.27 1135.06 1502.38 1193.72 1436.49 C 1235.22 1389.37 1265.61 1315.71 1260.02 1252.2 C 1250.14 1139.97 1152.01 1079.22 1063.53 1030.09 L 970.158 978.703 C 903.638 941.507 850.734 907.97 790.784 860.011 C 663.61 761.404 682.623 611.639 782.669 501.454 C 880.816 393.361 1018.27 325.179 1164.22 315.416 z"
              />
            </svg>
          </div>
          <div className="text-sm font-medium text-neutral-700 pt-1">Plan Generated</div>
        </div>

        {/* Plan Document Card */}
        <div className="rounded-2xl border border-neutral-200/80 shadow-sm bg-white overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-neutral-100">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-3">
              {plan.title}
            </h2>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-neutral-700">Goal:</span>
                <span>{plan.goal}</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-gray-300"></div>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-neutral-700">Timeframe:</span>
                <span>{plan.timeframe}</span>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="p-6 space-y-4">
            {plan.sections.map((section) => (
              <div 
                key={section.id} 
                className="rounded-xl bg-gray-50/80 border border-gray-100 p-5 transition-all hover:bg-gray-50 hover:border-gray-200"
              >
                {/* Section Header */}
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-neutral-800 mb-1">
                    {section.title}
                  </h3>
                  {section.summary && (
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {section.summary}
                    </p>
                  )}
                </div>

                {/* Steps */}
                <div className="space-y-2.5">
                  {section.steps.map((step) => (
                    <div 
                      key={step.id} 
                      className="flex items-start gap-3 group"
                    >
                      {/* Status Icon */}
                      <div className="mt-0.5 flex-shrink-0">
                        {getStatusIcon(step.status)}
                      </div>

                      {/* Step Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-neutral-800 leading-relaxed">
                              {step.label}
                            </p>
                            {step.details && (
                              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                                {step.details}
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-1.5">
                              {step.owner && (
                                <span className="text-xs text-gray-500">
                                  Owner: <span className="font-medium text-gray-700">{step.owner}</span>
                                </span>
                              )}
                              {step.duration && (
                                <>
                                  {step.owner && <span className="text-gray-300">•</span>}
                                  <span className="text-xs text-gray-500">
                                    Duration: <span className="font-medium text-gray-700">{step.duration}</span>
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Status Badge */}
                          {step.status && (
                            <div className="flex-shrink-0">
                              {getStatusBadge(step.status)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="px-6 pb-6">
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={handleSaveToSwirl}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm hover:shadow"
              >
                <Save className="w-4 h-4" />
                Save to Swirl
              </button>
              
              <button
                onClick={handleSaveToFocus}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <FolderOpen className="w-4 h-4" />
                Save to Focus
              </button>

              <button
                onClick={handleDuplicateInNewChat}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm hover:shadow"
              >
                <Copy className="w-4 h-4" />
                Duplicate in new chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}





